import type { IFileSizeUnitEnum } from "./enums";

import type { IIconName } from "@components/icon";

// eslint-disable-next-line @typescript-eslint/naming-convention
export type ID = string | number;

export interface IOption<T extends string | number = ID> {
  key: T;
  value: string;
  icon?: IIconName;
  flag?: string;
}

export interface IResponseOption<T extends string | number = ID> {
  id: T;
  name: string;
}

export interface IMetaOption<TMeta, TOption extends string | number = ID>
  extends IOption<TOption> {
  fullData?: TMeta;
  description?: string;
  imgUrl?: string;
}

export const EmptyNumberOption: IOption<number> = { key: 0, value: "" };
export const EmptyOption = { key: "" as const, value: "" as const };
// export const EmptyPhone: IPhoneNumber = { code: "+98", number: "" };

export interface IApiMapper<TApi, TData, TDto = TApi, TForm = TData> {
  fromApi: (data: TApi) => TData;
  toApi?: (data: TForm) => TDto;
}

export interface INotNullableFileObject {
  id: ID;
  name: string;
  type: string;
  size: number;
  url: string;
}

export type IFileObject = INotNullableFileObject | undefined | null;
export type IFileResponse =
  | {
      id: ID;
      url: string;
      name: string;
      mime_type: string;
      size_bytes: number;
      created_at: string;
      updated_at: string;
    }
  | undefined
  | null;

export interface IFile {
  id: ID;
  file: File;
}

export interface IDeleteActionParams {
  onDelete: (id: ID) => Promise<void> | void;
}

export interface IEditActionParams {
  onEdit: (id: ID) => Promise<void> | void;
}

/**
 * `idle`       => User has selected the file but didn't upload it yet. User can delete or upload it.
 *
 * `uploading`  => File is uploading. User can only cancel it.
 *
 * `success`    => File has uploaded successfully. User can only delete it.
 *
 * `error`      => An error occurred in uploading. User can delete or reupload it.
 *
 * `submitted`  => File has been uploaded in the past and we only have its uuid.
 * In this case, we have to request to the server with this uuid to receive the file information.
 * This is different from `idle` mode, you can not upload.
 */
export type IFileStatus =
  | "idle"
  | "uploading"
  | "success"
  | "error"
  | "submitted";

export interface IFileSize {
  size: number;
  unit: IFileSizeUnitEnum;
}

export type IPrefixWithCommon<T extends string> = `common:${T}`;
export type IWithId<T extends object> = { id: ID } & T;
export interface IPhoneNumber {
  code: string;
  number: string;
}

export type IFormProps<TSchema, TProps = unknown> = {
  isLoading?: boolean;
  onSubmit: (values: TSchema) => Promise<void> | void;
  onEdit: (values: Partial<TSchema>) => Promise<TSchema>;
  initialValues?: TSchema;
  isUpdatePending?: boolean;
} & TProps;

export interface IFormIdFieldProps {
  id: ID;
  onDelete?: (id: ID) => Promise<void>;
}
